/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */

import { Map, View } from 'ol';
import {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { Projection, get, transform as transformCoords } from 'ol/proj';
import { availableProjections } from 'configs/projections';
import { Group } from 'ol/layer';
import { ScaleLine } from 'ol/control';
import LayerName from './enums';
import MainAppContext from '../../MainAppContext';
import {
  initialCenter, initialZoom, maxZoom, minZoom,
} from '../../../configs/initialValues';
import zIndexes from '../../../configs/zIndexes';
import { ThemeContext } from '../../../layout';

function useGenerateMap() {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  // Lazy initializer: `new Map()` as a plain argument would build (and discard) a whole
  // OpenLayers map on every render.
  const [map, setMap] = useState<Map>(() => new Map());
  const { theme } = useContext(ThemeContext);
  const { latestView, currentProjection } = useContext(MainAppContext);

  const generateLayersGroup = () => {
    const geoLayer = new Group();
    geoLayer.set('name', LayerName.GeoLayer);
    return geoLayer;
  };

  const registerProjections = () => {
    availableProjections.forEach(({ epsg, config }) => {
      proj4.defs(epsg, config);
    });

    register(proj4);

    availableProjections.forEach(({ extent, epsg }) => {
      get(epsg)?.setExtent(extent);
    });
  };

  const generateMap = useCallback(async () => {
    registerProjections();
    const projection = get(availableProjections.find(
      (projection) => projection.name === currentProjection,
    )?.epsg as string);

    const scaleLine = new ScaleLine({
      units: 'metric',
      bar: false,
      steps: 2,
    });

    // Create view
    const view = new View({
      zoom: initialZoom,
      center: transformCoords(initialCenter, 'EPSG:4326', projection as Projection),
      projection: projection as Projection,
      minZoom,
      maxZoom,
    });

    // Create map
    const newMap = new Map({
      target: ref.current as HTMLDivElement,
      view,
      pixelRatio: 1,
      controls: undefined,
    });

    const basemaps = new Group({ layers: [] });
    const geoLayer = generateLayersGroup();
    basemaps.set('name', LayerName.BaseMapsLayer);
    geoLayer.set('name', LayerName.GeoLayer);
    basemaps.setZIndex(zIndexes.BASELAYERS);
    geoLayer.setZIndex(zIndexes.GEOLAYERS);

    newMap.addLayer(geoLayer);
    newMap.addLayer(basemaps);
    newMap.addControl(scaleLine);

    const latest = latestView.current.find((v) => v);

    if (latest) {
      newMap.getView().setCenter(latest.getCenter());
      newMap.getView().setZoom(latest.getZoom() as number);
    }

    mapRef.current = newMap;
    setMap(newMap);
  }, [currentProjection, latestView]);

  useEffect(() => {
    if (ref.current && !mapRef.current) {
      generateMap();
    }
  }, [generateMap]);

  // On unmount detach the map from the DOM (viewport, resize observer, event listeners) and
  // forget it, so a remount (or StrictMode's dev remount) builds a fresh one.
  useEffect(() => () => {
    mapRef.current?.setTarget(undefined);
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (map && currentProjection) {
      const projection = availableProjections.find(
        (projection) => projection.name === currentProjection,
      )?.epsg as string;
      const newProj = get(projection);
      const oldProj = map.getView().getProjection();

      // Change the map projection
      map.setView(new View({
        projection: newProj as Projection,
        center: transformCoords(
          map.getView().getCenter() || initialCenter,
          oldProj,
          newProj as Projection,
        ),
        zoom: map.getView().getZoom(),
        minZoom,
        maxZoom,
      }));
    }
  }, [currentProjection, generateMap, map]);

  useEffect(() => {
    const buttons = document.querySelectorAll('.ol-zoom button');
    buttons.forEach((btn) => {
      const button = btn as HTMLElement;
      if (theme === 'dark') {
        button.style.backgroundColor = '#001529';
        button.style.color = '#fff';
      } else {
        button.style.backgroundColor = '#fff';
        button.style.color = '#000';
      }
    });
  }, [theme]);

  return { map, mapContainer: ref };
}

export default useGenerateMap;
